import axios from "axios";

const baseUrl = "/api/v0/subscriptions";

export enum SubscriptionStatus {
    None = 0,
    Trialing = 1,
    Active = 2,
    PastDue = 3,
    Cancelled = 4,
    Complimentary = 5,
    Grace = 6,
}

export interface CardOnFile {
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
}

export enum BillingInterval {
    Month = 0,
    Year = 1,
}

export interface SubscribedPlan {
    title: string;
    price: number;
    interval: BillingInterval;
}

export interface Subscription {
    accountId: string;
    status: SubscriptionStatus;
    hasAccess: boolean;
    trialEndsAt: string | null;
    currentPeriodEnd: string | null;
    cancelAt: string | null;
    pastDueSince: string | null;
    graceUntil: string | null;
    plan: SubscribedPlan | null;
    card: CardOnFile | null;
}

export interface Price {
    amount: number;
    currency: string;
    interval: string;
    trialDays: number;
}

export interface SubscriptionConfig {
    publishableKey: string;
    price: Price | null;
}

export interface StartSubscriptionResult {
    subscription: Subscription;
    paymentClientSecret: string | null;
}

export interface Invoice {
    id: string;
    number: string | null;
    created: string;
    amount: number;
    currency: string;
    status: string;
    hostedUrl: string | null;
    pdfUrl: string | null;
}

const subscriptionApi = {
    getConfig: async (): Promise<SubscriptionConfig> => {
        const { data } = await axios.get<SubscriptionConfig>(`${baseUrl}/config`);
        return data;
    },

    getSubscription: async (accountId: string): Promise<Subscription> => {
        const { data } = await axios.get<Subscription>(`${baseUrl}/${accountId}`);
        return data;
    },

    createSetupIntent: async (accountId: string): Promise<string> => {
        const { data } = await axios.post<{ clientSecret: string }>(`${baseUrl}/${accountId}/setup-intent`);
        return data.clientSecret;
    },

    setPaymentMethod: async (accountId: string, paymentMethodId: string): Promise<Subscription> => {
        const { data } = await axios.put<Subscription>(`${baseUrl}/${accountId}/payment-method`, { paymentMethodId });
        return data;
    },

    start: async (accountId: string, paymentMethodId: string | null): Promise<StartSubscriptionResult> => {
        const { data } = await axios.post<StartSubscriptionResult>(`${baseUrl}/${accountId}`, { paymentMethodId });
        return data;
    },

    cancel: async (accountId: string): Promise<Subscription> => {
        const { data } = await axios.delete<Subscription>(`${baseUrl}/${accountId}`);
        return data;
    },

    reactivate: async (accountId: string): Promise<Subscription> => {
        const { data } = await axios.post<Subscription>(`${baseUrl}/${accountId}/reactivate`);
        return data;
    },

    refresh: async (accountId: string): Promise<Subscription> => {
        const { data } = await axios.post<Subscription>(`${baseUrl}/${accountId}/refresh`);
        return data;
    },

    getInvoices: async (accountId: string): Promise<Invoice[]> => {
        const { data } = await axios.get<Invoice[]>(`${baseUrl}/${accountId}/invoices`);
        return data;
    },
};

export type SubscriptionApi = typeof subscriptionApi;

export default subscriptionApi;
